/*
 * Soulog shared scene-card carousel (styles in assets/soulog-carousel.css)
 *
 * The home page's soulog-home-scenes and the carousel mode of the generic card version soulog-card-row share this single copy,
 * to avoid "fixed in one place, still broken in the other".
 *
 * Docking form (user requirement 2026-09-17, replacing 09-14's "first card left-aligned / last card right-aligned"):
 *   **every card is centered in the viewport**, with adjacent cards peeking on both sides; left of the first card and right of the last card have no neighbor, so that side is blank.
 *   The blank space comes from the track's left/right padding (see soulog-carousel.css), so scrollLeft=0 / =max are themselves the centered positions.
 *   Every 3 seconds of rest it auto-advances to the next card; after the last card, autoplay rewinds to the first card and starts over (no infinite loop).
 *   The interval can be overridden via <soulog-carousel data-autoplay-ms="…"> (2026-09-18, configurable in the admin for the generic card version);
 *   missing / invalid / less than 1 second all fall back to 3 seconds.
 *   data-autoplay="off" turns autoplay off, paging only via prev/next buttons / swiping (2026-09-26, for the multi-angle images of the color-switch showcase).
 *
 * The swiping itself is left to native overflow-x + scroll-snap (cards always center) —
 * touch and trackpads work naturally; this adds prev/next buttons, graying out at the ends, autoplay, and optional dots.
 *
 * Autoplay pauses in these cases: mouse hover / keyboard focus / finger held down / section not in viewport / tab hidden /
 * the system's "reduce motion" is on / a card is selected in the theme editor.
 *
 * Scroll-driven mode (optional, 2026-09-15, only for the home page's soulog-home-scenes):
 *   <soulog-carousel data-scroll-driven> sits inside [data-carousel-scroll-stage] (the sticky stage) within
 *   [data-carousel-scroll-root] (a tall box providing the scroll distance). The stage is pinned below the header, and as the page scrolls down the cards move horizontally with it;
 *   the pinned distance is split evenly into "card count - 1" segments, and once the last card is in place the pin is released, letting scrolling continue to the next section.
 *   The mapping is **linear** (changed 2026-09-18 after user feedback "scrolling isn't smooth"): card displacement is proportional to page scroll, moving at constant speed,
 *   starting as soon as it pins and releasing as soon as the last card is in place. Previously middle cards had a 15% dwell zone before and after + ease-in/ease-out,
 *   measured per 100px of scroll the card moved 0 → 150 → 270 → 3px, lurching and stopping, which reads as stutter — do not add dwell / easing back.
 *   During scrolling the prev/next buttons give direction feedback: "next" lights up when cards move forward, "previous" when moving back, and they go dark after stopping
 *   ([data-active="true"], styles in soulog-carousel.css).
 *   In this mode there is no autoplay; prev/next buttons / editor selection instead scroll the page to the corresponding position.
 *   Only active when SCROLL_QUERY matches (≥1024 and reduce motion off; the same query as soulog-home-scenes.css),
 *   otherwise it falls back to a normal carousel. The product page card version has no data-scroll-driven, so its behavior is unchanged.
 *
 * Video cards (user requirement 2026-09-17, a single card in the generic card version soulog-card-row can hold a video):
 *   when a card contains [data-carousel-video], **it plays only when its turn comes, muted, once, and stops on the last frame when done**;
 *   only the next time this card's turn comes again (or it scrolls out and back in) does it replay from the start; no looping or rewinding midway.
 *   The ended event is also the "this card has been watched" signal — the carousel doesn't count the 3 seconds, it waits until playback finishes before advancing,
 *   so the <video> must not have loop (it would never receive ended, and couldn't hold the last frame).
 *   Videos that won't play (load failure / browser refused autoplay) are flagged and fall back to the 3-second timer, so the carousel never gets stuck on one card.
 *   The vertical stack tier has no "next card": after playback it stays on the last frame, replaying only after scrolling out of the viewport and back in.
 *   Section scrolled out of the viewport / tab hidden / "reduce motion" on → everything pauses (saves power, especially on phones).
 *
 * Vertical stack tier (user requirement 2026-09-16, see the max-width:1023px block in soulog-carousel.css):
 *   at <1024 (phones + portrait tablets) carousels with .soulog-carousel--stack-mobile become a vertical column (both carousels have it),
 *   all cards shown and the page scrolls down normally.
 *   Pure CSS is enough: once the track becomes a vertical column scrollWidth = clientWidth, maxScroll() is 0,
 *   autoplay's canAutoplay() fails on its own, and the prev/next buttons are hidden by CSS as well.
 */
if (!customElements.get("soulog-carousel")) {
	const AUTOPLAY_MS = 3000;
	// Lower bound for the admin-configured interval: any shorter and it switches away before you can see it
	const AUTOPLAY_MIN_MS = 1000;
	// How long after the last scroll event counts as "stopped"; fallback when scrollend isn't supported
	const SETTLE_MS = 140;
	const VIDEO_SEL = "[data-carousel-video]";
	/*
	 * Pinning is desktop-only (≥1024): on 2026-09-16 phones and portrait tablets switched to showing all scene cards stacked vertically,
	 * and page scroll no longer drives the cards (replacing 2026-09-15's "phones pin too"; the 1024 tier was raised from 768 the same day
	 * after user feedback "tablets should match mobile" — an 820-wide portrait tablet can't fit
	 * the pinned form's cards in one screen). With "reduce motion" on it still falls back to a normal carousel.
	 * ⚠️ Must match the media query in soulog-home-scenes.css character for character.
	 */
	const SCROLL_QUERY =
		"(min-width: 1024px) and (orientation: landscape) and (prefers-reduced-motion: no-preference), (min-width: 1367px) and (prefers-reduced-motion: no-preference)";
	// Scroll mode: how long after the cards stop before the prev/next direction feedback goes dark
	const NAV_FEEDBACK_MS = 200;

	class SoulogCarousel extends HTMLElement {
		/** Pin progress (0~1) → the track's scrollLeft: linearly interpolate between adjacent dock points, passing each card at constant speed */
		static scrubLeft(progress, stops) {
			const segments = stops.length - 1;
			if (segments < 1) return stops[0] || 0;
			const s = Math.min(1, Math.max(0, progress)) * segments;
			const i = Math.min(Math.floor(s), segments - 1);
			return stops[i] + (stops[i + 1] - stops[i]) * (s - i);
		}

		connectedCallback() {
			this.track = this.querySelector("[data-carousel-track]");
			this.prev = this.querySelector("[data-carousel-prev]");
			this.next = this.querySelector("[data-carousel-next]");
			this.dots = this.querySelector("[data-carousel-dots]");
			if (!this.track) return;

			const autoplayMs = Number(this.getAttribute("data-autoplay-ms"));
			this.autoplayMs = autoplayMs >= AUTOPLAY_MIN_MS ? autoplayMs : AUTOPLAY_MS;
			this.autoplayOff = this.getAttribute("data-autoplay") === "off";
			this.hovering = false;
			this.focused = false;
			this.touching = false;
			this.editorPaused = false;
			this.inView = true;
			/*
			 * Three pieces of state for video cards (all keyed by the video element, so they're reclaimed automatically when a card is removed):
			 *   rewind  flag set on leaving the stage — next time on stage it plays from the start (the boundary between "stop on the last frame" and "replay when its turn comes again")
			 *   stuck   videos that won't play (play() rejected / error) — the carousel falls back to the 3-second timer instead of waiting for it
			 *   bound   ended / error listeners already bound, to avoid binding twice
			 */
			this.rewind = new WeakSet();
			this.stuck = new WeakSet();
			this.bound = new WeakSet();
			this.cardVisible = new WeakMap();
			this.reducedMotion = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : null;

			this.onPrev = () => this.step(-1);
			this.onNext = () => this.step(1);
			this.onScroll = () => {
				clearTimeout(this.settleTimer);
				if (!this.touching) this.settleTimer = setTimeout(this.onSettle, SETTLE_MS);
				this.sync();
			};
			this.onSettle = () => this.settle();
			this.onResize = () => {
				this.buildDots();
				this.scrub();
				this.sync();
				this.scheduleAutoplay();
			};
			// Page scroll → read geometry and write scrollLeft on the next frame, computing at most once per frame
			this.onPageScroll = () => {
				if (this.framePending) return;
				this.framePending = true;
				requestAnimationFrame(() => {
					this.framePending = false;
					this.scrub();
				});
			};
			this.onScrollMedia = () => {
				this.scrollDriven = Boolean(this.scrollMedia && this.scrollMedia.matches);
				this.scrub();
				this.sync();
				this.syncVideos();
				this.scheduleAutoplay();
			};
			this.onPointerEnter = (e) => {
				if (e.pointerType !== "mouse") return;
				this.hovering = true;
				this.pause();
			};
			this.onPointerLeave = (e) => {
				if (e.pointerType !== "mouse") return;
				this.hovering = false;
				this.scheduleAutoplay();
			};
			this.onTouchStart = () => {
				this.touching = true;
				this.pause();
			};
			this.onTouchEnd = () => {
				this.touching = false;
				this.onScroll();
			};
			// Only keyboard focus pauses: clicking the prev/next buttons with a mouse also focuses the button in Chrome, and it shouldn't stay paused after the mouse leaves
			this.onFocusIn = (e) => {
				if (!isFocusVisible(e.target)) return;
				this.focused = true;
				this.pause();
			};
			this.onFocusOut = (e) => {
				if (e.relatedTarget && this.contains(e.relatedTarget)) return;
				this.focused = false;
				this.scheduleAutoplay();
			};
			// Tab switched away: stop the videos too (syncVideos reads document.hidden itself)
			this.onVisibility = () => {
				this.syncVideos();
				this.scheduleAutoplay();
			};
			this.onBlockSelect = (e) => {
				const i = this.cards().indexOf(e.target);
				if (i < 0) return;
				this.editorPaused = true;
				this.pause();
				this.goTo(i);
			};
			this.onBlockDeselect = () => {
				this.editorPaused = false;
				this.scheduleAutoplay();
			};

			if (this.prev) this.prev.addEventListener("click", this.onPrev);
			if (this.next) this.next.addEventListener("click", this.onNext);
			this.track.addEventListener("scroll", this.onScroll, { passive: true });
			this.track.addEventListener("scrollend", this.onSettle);
			this.track.addEventListener("touchstart", this.onTouchStart, { passive: true });
			this.track.addEventListener("touchend", this.onTouchEnd, { passive: true });
			this.track.addEventListener("touchcancel", this.onTouchEnd, { passive: true });
			this.addEventListener("pointerenter", this.onPointerEnter);
			this.addEventListener("pointerleave", this.onPointerLeave);
			this.addEventListener("focusin", this.onFocusIn);
			this.addEventListener("focusout", this.onFocusOut);
			this.addEventListener("shopify:block:select", this.onBlockSelect);
			this.addEventListener("shopify:block:deselect", this.onBlockDeselect);
			document.addEventListener("visibilitychange", this.onVisibility);

			this.scrollRoot = this.hasAttribute("data-scroll-driven") ? this.closest("[data-carousel-scroll-root]") : null;
			this.scrollStage = this.scrollRoot ? this.scrollRoot.querySelector("[data-carousel-scroll-stage]") : null;
			this.scrollMedia = this.scrollStage && window.matchMedia ? window.matchMedia(SCROLL_QUERY) : null;
			this.scrollDriven = Boolean(this.scrollMedia && this.scrollMedia.matches);
			if (this.scrollMedia) {
				window.addEventListener("scroll", this.onPageScroll, { passive: true });
				if (this.scrollMedia.addEventListener) this.scrollMedia.addEventListener("change", this.onScrollMedia);
			}

			// Cards are lazy-loaded images, so the first-screen size may only settle after connectedCallback
			this.resizeObserver = new ResizeObserver(this.onResize);
			this.resizeObserver.observe(this.track);
			this.onResize();

			if ("IntersectionObserver" in window) {
				this.intersectionObserver = new IntersectionObserver(([entry]) => {
					this.inView = entry.isIntersecting;
					this.syncVideos();
					this.scheduleAutoplay();
				});
				this.intersectionObserver.observe(this);
				/*
				 * Observe each card individually, only for videos in the vertical stack tier: there all cards are expanded and there is no "docked card",
				 * so which one to play can only be determined by which is in the viewport. Threshold 0.5 = a card counts as its turn once more than half is visible
				 * (in the vertical stack, card height is 1/2~2/3 of the viewport, so more than half is the visual focus).
				 */
				this.cardObserver = new IntersectionObserver(
					(entries) => {
						entries.forEach((entry) => this.cardVisible.set(entry.target, entry.isIntersecting));
						this.syncVideos();
					},
					{ threshold: 0.5 },
				);
			}

			this.syncVideos();
		}

		disconnectedCallback() {
			this.pause();
			clearTimeout(this.settleTimer);
			clearTimeout(this.navFeedbackTimer);
			if (!this.track) return;
			if (this.prev) this.prev.removeEventListener("click", this.onPrev);
			if (this.next) this.next.removeEventListener("click", this.onNext);
			this.track.removeEventListener("scroll", this.onScroll);
			this.track.removeEventListener("scrollend", this.onSettle);
			this.track.removeEventListener("touchstart", this.onTouchStart);
			this.track.removeEventListener("touchend", this.onTouchEnd);
			this.track.removeEventListener("touchcancel", this.onTouchEnd);
			this.removeEventListener("pointerenter", this.onPointerEnter);
			this.removeEventListener("pointerleave", this.onPointerLeave);
			this.removeEventListener("focusin", this.onFocusIn);
			this.removeEventListener("focusout", this.onFocusOut);
			this.removeEventListener("shopify:block:select", this.onBlockSelect);
			this.removeEventListener("shopify:block:deselect", this.onBlockDeselect);
			document.removeEventListener("visibilitychange", this.onVisibility);
			if (this.scrollMedia) {
				window.removeEventListener("scroll", this.onPageScroll);
				if (this.scrollMedia.removeEventListener) this.scrollMedia.removeEventListener("change", this.onScrollMedia);
			}
			if (this.resizeObserver) this.resizeObserver.disconnect();
			if (this.intersectionObserver) this.intersectionObserver.disconnect();
			if (this.cardObserver) this.cardObserver.disconnect();
			// The card may still live elsewhere (the editor moved the section): stop the video before leaving, so it doesn't keep decoding in the background
			this.cards().forEach((card) => {
				const video = videoIn(card);
				if (video) this.holdVideo(video);
			});
		}

		cards() {
			return Array.from(this.track.querySelectorAll("[data-carousel-card]"));
		}

		maxScroll() {
			return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
		}

		/**
		 * Each card's dock scrollLeft, read from actual rendered values and independent of the current scroll position: always "card center line aligned to the container center line".
		 * First card = 0, last card = scroll end, because the track's left/right padding is exactly (container width − card width)/2 —
		 * when scrolled to either end the first/last card is itself dead center (changed 2026-09-17: previously first card left-aligned / last card right-aligned).
		 */
		stops() {
			const cards = this.cards();
			const max = this.maxScroll();
			const origin = this.track.getBoundingClientRect().left + this.track.clientWidth / 2;
			return cards.map((card, i) => {
				if (i === 0) return 0;
				if (i === cards.length - 1) return max;
				const rect = card.getBoundingClientRect();
				const center = this.track.scrollLeft + rect.left + rect.width / 2 - origin;
				return Math.min(Math.max(center, 0), max);
			});
		}

		/** Index of the dock point nearest to the current scroll position */
		currentIndex() {
			const current = this.track.scrollLeft;
			const stops = this.stops();
			let active = 0;
			stops.forEach((left, i) => {
				if (Math.abs(left - current) < Math.abs(stops[active] - current)) active = i;
			});
			return active;
		}

		/** Pinned scroll distance (px) and the stage's sticky top (= header height, varies with breakpoint / announcement bar, read live) */
		pinGeometry() {
			const stickyTop = parseFloat(getComputedStyle(this.scrollStage).top) || 0;
			const range = this.scrollRoot.offsetHeight - this.scrollStage.offsetHeight;
			return { stickyTop, range, rootTop: this.scrollRoot.getBoundingClientRect().top };
		}

		/** Scroll mode: position the cards according to the page position */
		scrub() {
			if (!this.scrollDriven) return;
			const { stickyTop, range, rootTop } = this.pinGeometry();
			const progress = range > 0 ? Math.min(1, Math.max(0, (stickyTop - rootTop) / range)) : 0;
			const left = SoulogCarousel.scrubLeft(progress, this.stops());
			const delta = left - this.track.scrollLeft;
			if (Math.abs(delta) < 0.5) return;
			this.track.scrollLeft = left;
			this.showNavFeedback(delta > 0 ? 1 : -1);
		}

		/** Scroll mode: light up the button matching the direction the cards move, turn it off NAV_FEEDBACK_MS after stopping */
		showNavFeedback(direction) {
			if (this.next) this.next.setAttribute("data-active", direction > 0 ? "true" : "false");
			if (this.prev) this.prev.setAttribute("data-active", direction < 0 ? "true" : "false");
			clearTimeout(this.navFeedbackTimer);
			this.navFeedbackTimer = setTimeout(() => {
				if (this.next) this.next.setAttribute("data-active", "false");
				if (this.prev) this.prev.setAttribute("data-active", "false");
			}, NAV_FEEDBACK_MS);
		}

		/** Scroll mode: scroll the page to the position where card i is exactly docked */
		scrollPageTo(i) {
			const segments = this.cards().length - 1;
			if (segments < 1) return;
			const { stickyTop, range, rootTop } = this.pinGeometry();
			const top = window.scrollY + rootTop - stickyTop + (i / segments) * range;
			window.scrollTo({ top, behavior: "smooth" });
		}

		/** Jump by dock points rather than a fixed-step scrollBy — otherwise it would land between two cards */
		step(direction) {
			if (this.scrollDriven) {
				// Base it on the card currently in view: clicking previous while in a dwell zone should go back to the previous card, not re-center in place
				const target = this.currentIndex() + direction;
				if (target >= 0 && target < this.cards().length) this.scrollPageTo(target);
				return;
			}
			const current = this.track.scrollLeft;
			const stops = this.stops();
			const target =
				direction > 0 ? stops.find((left) => left > current + 1) : stops.reverse().find((left) => left < current - 1);
			if (target === undefined) return;
			this.track.scrollTo({ left: target, behavior: "smooth" });
			this.scheduleAutoplay();
		}

		goTo(i) {
			if (this.scrollDriven) {
				this.scrollPageTo(i);
				return;
			}
			const target = this.stops()[i];
			if (target !== undefined) this.track.scrollTo({ left: target, behavior: "smooth" });
		}

		/** Scroll stopped: update buttons / dots, switch to the video that should play, restart the 3-second countdown */
		settle() {
			if (this.touching) return;
			clearTimeout(this.settleTimer);
			this.sync();
			this.syncVideos();
			this.scheduleAutoplay();
		}

		/**
		 * Put the videos on and off stage in their places. Called only once scrolling has **settled** (not on every scroll event),
		 * so fast swipes don't interrupt decoding with play/pause all along the way.
		 * Who counts as "on stage": horizontal tier = the docked card; vertical stack tier (track doesn't overflow horizontally) = those more than half visible.
		 */
		syncVideos() {
			const cards = this.cards();
			const stacked = this.maxScroll() <= 0;
			const active = stacked ? -1 : this.currentIndex();
			// Scroll-driven mode (the home page tier) still plays the card on stage, it just doesn't advance after playback — there, switching is driven by page scroll
			const awake = this.inView && !document.hidden && !(this.reducedMotion && this.reducedMotion.matches);
			cards.forEach((card, i) => {
				const video = videoIn(card);
				if (!video) return;
				this.bindVideo(card, video);
				const onStage = stacked ? this.cardVisible.get(card) === true : i === active;
				if (awake && onStage) this.playVideo(video);
				else this.holdVideo(video);
				/*
				 * Pre-warm neighboring cards: if buffering starts from zero only when its turn comes, the cover would stall first.
				 * Off-stage ones revert to none — downloads already started won't be aborted by this, but no new bytes are actively fetched.
				 */
				video.preload = onStage || (!stacked && Math.abs(i - active) === 1) ? "auto" : "none";
			});
		}

		/** Bind ended / error once, and hand the card to the per-card observer (used by the vertical stack tier to judge "who is in the viewport") */
		bindVideo(card, video) {
			if (this.bound.has(video)) return;
			this.bound.add(video);
			video.addEventListener("ended", () => this.onVideoEnd(video));
			video.addEventListener("error", () => this.markStuck(video));
			if (this.cardObserver) this.cardObserver.observe(card);
		}

		/**
		 * On stage: play once from the start.
		 * Ones that have already finished **don't auto-replay** — stopping on the last frame is the intended effect; only after leaving the stage (holdVideo set the rewind flag)
		 * and coming back does it return to 0 and replay.
		 */
		playVideo(video) {
			if (this.rewind.has(video)) {
				this.rewind.delete(video);
				try {
					video.currentTime = 0;
				} catch (_) {
					/* No seekable timeline yet: calling play next starts from the beginning anyway */
				}
			} else if (video.ended) {
				return;
			}
			if (!video.paused) return;
			const played = video.play && video.play();
			// Autoplay refused by the browser (rare: these videos are all muted + playsinline) → treat as unplayable, don't let the carousel get stuck
			if (played && typeof played.catch === "function") played.catch(() => this.markStuck(video));
		}

		/** Off stage: pause (the last frame stays as is, no rewind), and record "play from the start next time on stage" */
		holdVideo(video) {
			if (!video.paused && video.pause) video.pause();
			this.rewind.add(video);
		}

		/** Won't play: the carousel stops waiting for its ended and falls back to the 3-second timer */
		markStuck(video) {
			this.stuck.add(video);
			this.scheduleAutoplay();
		}

		/**
		 * Playback finished. The picture **doesn't change at all** (stays on the last frame); in the horizontal tier this is also the "this card has been watched" signal,
		 * so advance to the next card. Don't advance while hovered / focused / tab hidden — scheduleAutoplay takes over after recovery
		 * (by then video.ended is already true, so it takes the 3-second timer instead of waiting forever).
		 */
		onVideoEnd(video) {
			if (this.maxScroll() <= 0 || this.scrollDriven) return;
			if (videoIn(this.cards()[this.currentIndex()]) !== video) return;
			if (!this.canAutoplay()) return;
			this.advance();
		}

		canAutoplay() {
			return (
				!this.autoplayOff &&
				!this.scrollDriven &&
				this.maxScroll() > 0 &&
				this.inView &&
				!this.hovering &&
				!this.focused &&
				!this.touching &&
				!this.editorPaused &&
				!document.hidden &&
				!(this.reducedMotion && this.reducedMotion.matches)
			);
		}

		pause() {
			clearTimeout(this.autoTimer);
			this.autoTimer = null;
		}

		/** Advance to the next card; if already on the last card, rewind to the first and start over */
		advance() {
			if (this.track.scrollLeft >= this.maxScroll() - 1) {
				this.track.scrollTo({ left: 0, behavior: "smooth" });
				this.scheduleAutoplay();
			} else {
				this.step(1);
			}
		}

		/**
		 * Restart the 3-second count from now. settle calls this again when the switch animation ends, so each card actually rests a full 3 seconds.
		 * When the card on stage is **a video card that is playing**, no timer runs — wait for it to finish (ended → advance), so the video is always watched in full.
		 * Finished (ended) and unplayable (stuck) ones still count 3 seconds, otherwise the carousel would stay on that card forever.
		 */
		scheduleAutoplay() {
			this.pause();
			if (!this.canAutoplay()) return;
			const video = videoIn(this.cards()[this.currentIndex()]);
			if (video && !video.ended && !this.stuck.has(video)) return;
			this.autoTimer = setTimeout(() => {
				if (!this.canAutoplay()) return;
				this.advance();
			}, this.autoplayMs);
		}

		/** Dot count = card count; rebuilt only when the card count changes */
		buildDots() {
			if (!this.dots) return;
			const count = this.cards().length;
			if (this.dots.children.length === count) return;
			this.dots.textContent = "";
			for (let i = 0; i < count; i += 1) {
				const dot = document.createElement("button");
				dot.type = "button";
				dot.className = "soulog-carousel__dot";
				dot.setAttribute("aria-label", String(i + 1));
				dot.addEventListener("click", () => this.goTo(i));
				this.dots.appendChild(dot);
			}
		}

		sync() {
			const max = this.maxScroll();
			// 1px tolerance: with subpixel widths scrollLeft can't reach a max that is strictly equal
			const atStart = this.track.scrollLeft <= 1;
			const atEnd = this.track.scrollLeft >= max - 1;
			if (this.prev) this.prev.disabled = atStart;
			if (this.next) this.next.disabled = atEnd || max <= 0;

			if (this.dots && this.dots.children.length) {
				const active = this.currentIndex();
				Array.from(this.dots.children).forEach((dot, i) => {
					dot.setAttribute("aria-current", i === active ? "true" : "false");
				});
			}
		}
	}

	/** The video in a card (null if none). The card may be swapped out by the editor, so look it up fresh each time instead of caching */
	function videoIn(card) {
		return card && card.querySelector ? card.querySelector(VIDEO_SEL) : null;
	}

	function isFocusVisible(el) {
		try {
			return el.matches(":focus-visible");
		} catch (_) {
			return true;
		}
	}

	customElements.define("soulog-carousel", SoulogCarousel);
}
